// react-native-svg.web.tsx
import React, { forwardRef } from "react";
import { StyleSheet } from "react-native";

/** Normaliza props e ACHATA style (evita "Cannot set indexed properties...") */
function normalizeProps<P extends Record<string, any>>(props: P) {
  const { onPress, accessibilityLabel, style, ...rest } = props || {};
  const webProps: any = { ...rest };

  // RN -> Web
  if (onPress) webProps.onClick = onPress;
  if (accessibilityLabel) webProps["aria-label"] = accessibilityLabel;

  // 🔧 AQUI: garante que style não seja array
  if (style !== undefined) {
    webProps.style = StyleSheet.flatten(style) || undefined;
  }

  return webProps;
}

/** Helper para criar wrapper de elemento SVG nativo do DOM */
function createSvgComponent<T extends keyof JSX.IntrinsicElements>(tag: T) {
  return forwardRef<any, any>((props, ref) =>
    React.createElement(tag, { ref, ...normalizeProps(props) })
  );
}

/** Elemento raiz <svg> */
export const Svg = forwardRef<SVGSVGElement, any>((props, ref) => {
  return <svg ref={ref} {...normalizeProps(props)} />;
});

/** Elementos gráficos comuns */
export const G = createSvgComponent("g");
export const Path = createSvgComponent("path");
export const Rect = createSvgComponent("rect");
export const Circle = createSvgComponent("circle");
export const Ellipse = createSvgComponent("ellipse");
export const Line = createSvgComponent("line");
export const Polyline = createSvgComponent("polyline");
export const Polygon = createSvgComponent("polygon");

/** Definições e clipes */
export const Defs = createSvgComponent("defs");
export const ClipPath = createSvgComponent("clipPath");

/** Gradientes */
export const LinearGradient = createSvgComponent("linearGradient");
export const RadialGradient = createSvgComponent("radialGradient");
export const Stop = createSvgComponent("stop");

/** Texto */
export const Text = createSvgComponent("text");
export const TSpan = createSvgComponent("tspan");

/** Uso e imagens internas ao SVG */
export const Use = createSvgComponent("use");
export const Image = forwardRef<any, any>((props, ref) => {
  return <image ref={ref} {...normalizeProps(props)} />;
});

/** SvgUri — carrega SVG remoto via <img> */
type SvgUriProps = {
  uri?: string;
  source?: { uri: string };
  width?: number | string;
  height?: number | string;
  style?: React.CSSProperties | any;
  preserveAspectRatio?: string;
};
export const SvgUri = forwardRef<HTMLImageElement, SvgUriProps>((props, ref) => {
  const { uri, source, ...rest } = props;
  const src = uri ?? source?.uri ?? "";
  const p = normalizeProps(rest);
  return <img ref={ref} src={src} alt="" {...p} />;
});

/** SvgXml — insere SVG inline (permite alterar fill/stroke via CSS) */
export const SvgXml = forwardRef<
  SVGSVGElement,
  { xml: string; width?: number | string; height?: number | string; style?: React.CSSProperties | any }
>(({ xml, ...rest }, ref) => {
  const p = normalizeProps(rest);
  // Usamos <span> como contêiner; o XML deve conter o <svg> em si.
  return (
    <span
      ref={ref as any}
      {...p}
      style={{ display: "inline-block", lineHeight: 0, ...(p.style || {}) }}
      dangerouslySetInnerHTML={{ __html: xml }}
    />
  );
});

/** Export default compatível — deve ser o componente Svg em si,
 *  pois react-native-svg exporta `export default Svg` no pacote real. */
export default Svg;
